import { apiFetch } from './api.js';

/** RBAC & User Management (spec §13.1). */

export interface DirectoryGrant {
  id: string;
  workspaceRoleId: string;
  label: string;
  app: string;
  businessUnitIds: string[];
  projectIds: string[];
  engagementIds: string[];
  expiresAt: string | null;
  /** Computed server-side: an expired grant is no grant at all (ADR-0005). */
  isExpired: boolean;
}

export interface DirectoryUser {
  id: string;
  username: string;
  email: string;
  displayName: string;
  designation: string | null;
  platformRoleId: string | null;
  platformRoleLabel: string | null;
  status: string;
  lastAccessAt: string | null;
  authSource: string;
  workspaceRoles: DirectoryGrant[];
}

export interface RoleRow {
  id: string;
  key: string | null;
  label: string;
  description: string | null;
  app?: string;
  isSystem: boolean;
  permissions: Record<string, { scope: string; only?: string }>;
}

export interface DelegationRow {
  id: string;
  fromUserId: string;
  toUserId: string;
  app: string | null;
  startsAt: string;
  endsAt: string;
  reason: string | null;
  revokedAt: string | null;
}

export interface HistoryRow {
  id: string;
  action: string;
  entityType: string;
  entityLabel: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  occurredAt: string;
  actorLabel: string | null;
  actorUserId: string | null;
}

export interface RbacSnapshot {
  users: DirectoryUser[];
  platformRoles: RoleRow[];
  workspaceRoles: RoleRow[];
  delegations: DelegationRow[];
  history: HistoryRow[];
}

export interface BulkGrantRow {
  line: number;
  username: string;
  roleKey: string;
  businessUnitCodes: string[];
  projectCodes: string[];
  expiresAt: string | null;
  problems: string[];
}

export interface BulkGrantReport {
  dryRun: boolean;
  rows: BulkGrantRow[];
  valid: number;
  invalid: number;
  applied: boolean;
}

export const fetchRbac = () => apiFetch<RbacSnapshot>('/admin/rbac');

export interface PasswordResetResult {
  user: { id: string; username: string; displayName: string };
  /** Shown once. Stored nowhere but as a hash, and absent from the audit log. */
  temporaryPassword: string;
  revokedSessions: number;
}

export const resetPassword = (userId: string) =>
  apiFetch<PasswordResetResult>(`/admin/rbac/users/${userId}/reset-password`, { method: 'POST' });

export interface CreatedAccount {
  user: { id: string; username: string; email: string; displayName: string };
  /** Shown once. Stored nowhere but as a hash, and absent from the audit log. */
  temporaryPassword: string;
}

export const createAccount = (body: {
  username: string;
  email: string;
  displayName: string;
  designation?: string | null;
  platformRoleId: string;
}) => apiFetch<CreatedAccount>('/admin/rbac/users', { method: 'POST', body: JSON.stringify(body) });

export const setPlatformRole = (userId: string, platformRoleId: string | null) =>
  apiFetch<{ user: DirectoryUser }>(`/admin/rbac/users/${userId}/platform-role`, {
    method: 'PATCH',
    body: JSON.stringify({ platformRoleId }),
  });

export const grantRole = (body: Record<string, unknown>) =>
  apiFetch<{ grant: DirectoryGrant }>('/admin/rbac/grants', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateGrantScope = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ grant: DirectoryGrant }>(`/admin/rbac/grants/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const revokeGrant = (id: string) =>
  apiFetch<{ revoked: { id: string } }>(`/admin/rbac/grants/${id}`, { method: 'DELETE' });

export const createWorkspaceRole = (body: Record<string, unknown>) =>
  apiFetch<{ role: RoleRow }>('/admin/rbac/workspace-roles', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateRolePermissions = (
  id: string,
  permissions: Record<string, { scope: string; only?: string }>,
) =>
  apiFetch<{ role: RoleRow }>(`/admin/rbac/workspace-roles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ permissions }),
  });

/** A delegation of the caller's own authority. Dates are calendar dates, read in the organisation's timezone. */
export const createDelegation = (body: {
  toUserId: string;
  app: string | null;
  businessUnitIds: string[];
  projectIds: string[];
  startsAt: string;
  endsAt: string;
  reason: string;
}) => apiFetch<{ delegation: DelegationRow }>('/admin/rbac/delegations', { method: 'POST', body: JSON.stringify(body) });

/** One's own delegations, both ways, and who one may delegate to (NEW-10). */
export interface MyDelegations {
  delegations: {
    id: string;
    direction: 'given' | 'received';
    otherParty: string | null;
    app: string | null;
    startsAt: string;
    endsAt: string;
    reason: string;
    revokedAt: string | null;
  }[];
  delegateApps: string[];
  people: { id: string; displayName: string }[];
}

export const fetchMyDelegations = () => apiFetch<MyDelegations>('/admin/rbac/my-delegations');

export const revokeDelegation = (id: string) =>
  apiFetch<{ revoked: { id: string } }>(`/admin/rbac/delegations/${id}`, { method: 'DELETE' });

export const bulkGrants = (csv: string, dryRun: boolean) =>
  apiFetch<{ report: BulkGrantReport }>('/admin/rbac/bulk-grants', {
    method: 'POST',
    body: JSON.stringify({ csv, dryRun }),
  });
